import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import {
  Trophy,
  Award,
  Star,
  Sparkles,
  CheckCircle,
  Home,
  BookOpen,
  LayoutDashboard,
  ArrowLeft,
  Share2,
  Twitter,
  Facebook,
  Linkedin,
  Mail,
  X,
  Volume2,
  VolumeX,
  Rocket,
  Heart,
  Sun,
  PartyPopper,
  Zap,
} from "lucide-react";
import { coursesApi, enrollmentApi, gamificationApi } from "../lib";
import { showSuccess, showError } from "../lib/toast";

interface Course {
  _id: string;
  title: string;
  thumbnail?: string;
  instructorName?: string;
  certificate?: boolean;
  certificateExam?: string;
}

interface ExamData {
  hasCertificateExam: boolean;
  certificateExamPassed: boolean;
  certificateExamRequired: boolean;
}

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.2 },
  },
};

const scaleBounce = {
  hidden: { scale: 0 },
  visible: {
    scale: 1,
    transition: { type: "spring", stiffness: 300, damping: 22, delay: 0.2 },
  },
};

export default function CourseFinishPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<Course | null>(null);
  const [examData, setExamData] = useState<ExamData | null>(null);
  const [certificateIssued, setCertificateIssued] = useState(false);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [fireworks, setFireworks] = useState(true);

  const triggerCelebrations = useCallback(() => {
    try {
      confetti({
        particleCount: 80,
        spread: 55,
        origin: { y: 0.6 },
        colors: ["#f59e0b", "#ef4444", "#10b981", "#3b82f6", "#8b5cf6"],
        disableForReducedMotion: true,
      });

      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 45,
          origin: { x: 0, y: 0.5 },
          colors: ["#10b981", "#3b82f6", "#8b5cf6"],
          disableForReducedMotion: true,
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 45,
          origin: { x: 1, y: 0.5 },
          colors: ["#f59e0b", "#ef4444", "#ec4899"],
          disableForReducedMotion: true,
        });
      }, 200);
    } catch (err) {
      console.log("Confetti error:", err);
    }
  }, []);

  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const audio = new Audio(
        "https://assets.mixkit.co/active_storage/sfx/2002/2002-preview.m4a",
      );
      audio.volume = 0.15;
      audio.play().catch(() => {});
    } catch (err) {
      console.log("Audio play failed:", err);
    }
  }, [soundEnabled]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const [courseRes, enrollmentRes] = await Promise.all([
          coursesApi.getCourseById(id),
          enrollmentApi.getCourseEnrollment(id),
        ]);

        if (courseRes.success) setCourse(courseRes.data);
        if (enrollmentRes.success) {
          const enrollment = enrollmentRes.data;
          if (enrollment.progress < 100) {
            showError("Complete all lessons first!");
            navigate(`/course/${id}/learn`);
            return;
          }
          setExamData({
            hasCertificateExam: enrollment.hasCertificateExam || false,
            certificateExamPassed: enrollment.certificateExamPassed || false,
            certificateExamRequired: enrollment.certificateExamRequired || false,
          });
          setCertificateIssued(enrollment.certificateIssued || false);
        }
      } catch (err) {
        console.error("Failed to load data:", err);
        showError("Failed to load course data");
      } finally {
        setLoading(false);
        setTimeout(() => triggerCelebrations(), 300);
        setTimeout(() => playSound(), 200);
      }
    };
    fetchData();
  }, [id, triggerCelebrations, playSound]);

  const handleTakeExam = () => navigate(`/course/${id}/certificate-exam`);
  const handleViewCertificate = () => {
    if (id) {
      navigate(`/certificate?courseId=${id}`);
    } else {
      showError("Certificate not available");
    }
  };
  const handleBackToCourse = () => navigate(`/course/${id}`);
  const handleDashboard = () => navigate("/dashboard/student/courses");
  
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = `I just completed "${course?.title}" on LearnHub! 🎉`;
    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent("Course Completion!")}&body=${encodeURIComponent(text + "\n\n" + url)}`,
    };
    window.open(shareUrls[platform], "_blank", "width=600,height=400");
    setShowShareMenu(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="px-4 text-center">
          <div className="w-10 h-10 mx-auto mb-3 border-2 border-orange-500 rounded-full border-t-transparent animate-spin" />
          <p className="text-xs text-gray-500">Preparing your celebration...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="mb-1 text-sm text-gray-600">Course not found</p>
          <Link to="/" className="text-xs text-orange-600 hover:underline">
            Go back home
          </Link>
        </div>
      </div>
    );
  }

  const hasExam = examData?.hasCertificateExam || false;
  const examPassed = examData?.certificateExamPassed || false;
  const canGetCertificate = certificateIssued || examPassed || !hasExam;

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-0.5 h-0.5 bg-orange-300/30 rounded-full"
            initial={{ x: Math.random() * 100 + "%", y: -10 }}
            animate={{ y: "100vh" }}
            transition={{
              duration: Math.random() * 8 + 4,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      {/* Header */}
      <div className="sticky top-0 z-20 border-b bg-white/95 backdrop-blur-md border-gray-200">
        <div className="max-w-4xl mx-auto px-3 py-2.5 flex items-center justify-between md:px-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Link
              to={`/course/${id}/learn`}
              className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <span className="text-[11px] text-gray-500 md:text-xs">
              Course Completed
            </span>
          </div>
          <div className="flex items-center gap-1 md:gap-2">
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </button>
            <Link
              to="/dashboard/student/courses"
              className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" />
            </Link>
            <Link
              to="/"
              className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
            >
              <Home className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-50px)] px-3 py-5 md:px-4 md:py-6">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="w-full max-w-md space-y-4 md:max-w-lg md:space-y-5"
        >
          {/* Main Celebration Card */}
          <motion.div
            variants={fadeInUp}
            className="relative p-5 overflow-hidden text-center bg-white border shadow-lg rounded-xl border-gray-100 md:p-6 md:rounded-2xl"
          >
            {/* Animated Gradient Border */}
            <motion.div
              className="absolute inset-0 pointer-events-none rounded-xl md:rounded-2xl"
              style={{
                background:
                  "linear-gradient(90deg, #f59e0b, #ef4444, #10b981, #3b82f6, #8b5cf6, #f59e0b)",
                backgroundSize: "300% 100%",
                opacity: 0.1,
              }}
              animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            />

            {/* Trophy Section */}
            <motion.div
              variants={scaleBounce}
              className="relative mx-auto mb-3 md:mb-4"
            >
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex items-center justify-center mx-auto rounded-full shadow-lg w-14 h-14 bg-gradient-to-br from-orange-600 to-orange-500 md:w-16 md:h-16"
              >
                <Trophy className="text-white w-6 h-6 md:w-7 md:h-7" />
              </motion.div>
            </motion.div>

            {/* Title */}
            <motion.h1
              variants={fadeInUp}
              className="mb-1 text-lg font-bold text-transparent md:text-xl bg-clip-text bg-gradient-to-r from-orange-600 to-orange-500"
            >
              Congratulations!
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="mb-1 text-xs text-gray-600 md:text-sm"
            >
              You've completed{" "}
              <span className="font-semibold text-gray-900">{course?.title}</span>
            </motion.p>

            <motion.p
              variants={fadeInUp}
              className="text-[11px] text-gray-500 mb-4 md:text-xs md:mb-5"
            >
              Amazing work! You're now ready for the next challenge.
            </motion.p>

            {/* Stats Grid */}
            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-2 gap-2 mb-4 md:gap-3 md:mb-5"
            >
              <motion.div
                variants={fadeInUp}
                whileHover={{ scale: 1.02 }}
                className="p-2.5 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 md:p-3"
              >
                <CheckCircle className="w-4 h-4 mx-auto mb-1 text-emerald-500" />
                <p className="text-sm font-bold text-gray-900 md:text-base">
                  100%
                </p>
                <p className="text-[9px] text-gray-500 md:text-[10px]">
                  Completed
                </p>
              </motion.div>
              <motion.div
                variants={fadeInUp}
                whileHover={{ scale: 1.02 }}
                className="p-2.5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200 md:p-3"
              >
                <Award className="w-4 h-4 mx-auto mb-1 text-amber-500" />
                <p className="text-sm font-bold text-gray-900 md:text-base">
                  {hasExam && !examPassed ? "Pending" : "Earned"}
                </p>
                <p className="text-[9px] text-gray-500 md:text-[10px]">
                  Certificate
                </p>
              </motion.div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              variants={fadeInUp}
              className="flex flex-col justify-center gap-2 sm:flex-row sm:gap-3"
            >
              {hasExam && !examPassed ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleTakeExam}
                    className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-orange-600 to-orange-500 rounded-lg hover:shadow-md transition-all"
                  >
                    <PartyPopper className="w-3.5 h-3.5" />
                    Take Exam
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDashboard}
                    className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    My Courses
                  </motion.button>
                </>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleViewCertificate}
                    className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-lg hover:shadow-md transition-all"
                  >
                    <Award className="w-3.5 h-3.5" />
                    View Certificate
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="flex items-center justify-center gap-1.5 w-full sm:w-auto px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    Share
                  </motion.button>
                </>
              )}
            </motion.div>

            {/* Certificate Notice */}
            {hasExam && !examPassed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg md:mt-4"
              >
                <div className="flex items-center justify-center gap-1.5 text-amber-600">
                  <Zap className="w-3 h-3" />
                  <p className="text-[10px] md:text-[11px]">
                    Complete the exam to earn your certificate!
                  </p>
                </div>
              </motion.div>
            )}

            {hasExam && examPassed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg md:mt-4"
              >
                <div className="flex items-center justify-center gap-1.5 text-emerald-600">
                  <Trophy className="w-3 h-3" />
                  <p className="text-[10px] md:text-[11px]">
                    Certificate ready! Click View Certificate to see it.
                  </p>
                </div>
              </motion.div>
            )}

            {!hasExam && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg md:mt-4"
              >
                <div className="flex items-center justify-center gap-1.5 text-emerald-600">
                  <Trophy className="w-3 h-3" />
                  <p className="text-[10px] md:text-[11px]">
                    Course completed! View your certificate now.
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Celebration Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-center space-y-1.5"
          >
            <p className="text-[10px] text-gray-500 md:text-[11px]">
              Share your achievement and inspire others!
            </p>
            <div className="flex justify-center gap-2 md:gap-3">
              {[Heart, Rocket, Sun].map((Icon, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.1 }}
                  className="p-1.5 bg-gray-100 rounded-full text-gray-500 hover:text-rose-500 transition-colors"
                >
                  <Icon className="w-3.5 h-3.5" />
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Share Menu Modal */}
      <AnimatePresence>
        {showShareMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
            onClick={() => setShowShareMenu(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-xs p-4 bg-white border border-gray-200 rounded-xl shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  Share Achievement
                </h3>
                <button
                  onClick={() => setShowShareMenu(false)}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: Twitter, name: "Twitter", color: "hover:bg-[#1DA1F2]/10" },
                  { icon: Facebook, name: "Facebook", color: "hover:bg-[#4267B2]/10" },
                  { icon: Linkedin, name: "LinkedIn", color: "hover:bg-[#0077B5]/10" },
                  { icon: Mail, name: "Email", color: "hover:bg-gray-100" },
                ].map(({ icon: Icon, name, color }) => (
                  <button
                    key={name}
                    onClick={() => handleShare(name.toLowerCase())}
                    className={`flex items-center gap-2 p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors ${color}`}
                  >
                    <Icon className="w-4 h-4 text-gray-600" />
                    <span className="text-xs text-gray-700">{name}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}